/* eslint-disable import/no-internal-modules */
import { IdpType } from 'api/account';
import { HttpClient } from 'api/http-client';
import { AuthenticationType } from 'api/models';
import { ApiKeyCreateResponse } from 'services/api-key';

export interface SamlConfig {
  accountIdpConfigId: number;
  idpId: string;
  idpType: IdpType;
  idpProviderType: string;
  idpMeta: {
    ssoUrl: string;
    audienceUrl: string;
    keyId: string;
    certificate: string;
  };
  saml: {
    samlIdpName: string;
    ssoUrl: string;
    audienceUrl: string;
    certificate: string;
    keyId: string;
  };
  accountId: boolean;
  enabled: boolean;
}

export type CreateSsoPayload = {
  idpType?: IdpType;
  enabled?: boolean;
  saml?: {
    ssoUrl: string;
    audienceUrl: string;
    certificate: string;
  };
};

export interface UpdateSsoPayload {
  idpId: string;
  idpType?: IdpType;
  enabled?: boolean;
  saml?: {
    ssoUrl?: string;
    audienceUrl?: string;
    certificate?: string;
  };
}

export type UpsertSsoPayload = CreateSsoPayload & { idpId?: string };

export interface SamlMeta {
  idpId: string;
  entityId: string;
  acsUrl: string;
  certificate: string;
}

export enum LoginPolicyType {
  RocketlaneAuth = 'ROCKETLANE_AUTH',
  MagicLink = 'MAGIC_LINK',
  Idp = 'IDP',
}

interface BaseLoginPolicyPayload {
  companyId: number;
  authType: LoginPolicyType;
}

interface IdpLoginPolicyPayload {
  companyId: number;
  authType: LoginPolicyType;
  idpConfigId: number;
}

export type LoginPolicyPayload = BaseLoginPolicyPayload | IdpLoginPolicyPayload;

const resourceUrl = '/accounts/idp';
const loginPolicyUrl = '/companies/authentication';
const triggerOtpUrl = 'users/trigger-otp?token=';
const validateOtpUrl = 'users/validate-otp?token=';

const samlUrl = (idpId: string) => `${resourceUrl}/${idpId}`;

class IdpHttpClient extends HttpClient {
  public getAllIdp = (): Promise<Array<SamlConfig>> => {
    return this.get(resourceUrl);
  };

  public createIdpWithSSO = (
    payload: CreateSsoPayload,
  ): Promise<SamlConfig> => {
    return this.post(resourceUrl, {
      ...payload,
      enabled: false,
    });
  };

  public updateIdpWithSSO = (payload: UpdateSsoPayload): Promise<null> => {
    return this.put(resourceUrl, {
      ...payload,
    });
  };

  public getSamlMeta = (idpId?: string): Promise<SamlMeta> | undefined => {
    if (!idpId) {
      return undefined;
    }

    return this.get(`${samlUrl(idpId)}/metadata`);
  };

  public getSamlMetaDownload = (idpId: string) => {
    return this.get<{ attachment: { location: string } }>(
      `/accounts/idp/${idpId}/metadata/download`,
    ).then((val) => val.attachment.location);
  };

  public getLoginPolicy = (): Promise<Array<AuthenticationType>> => {
    return this.get(loginPolicyUrl);
  };

  public updateLoginPolicy = (
    payload: Array<LoginPolicyPayload>,
  ): Promise<Array<AuthenticationType>> => {
    return this.post(loginPolicyUrl, payload);
  };

  public TriggerOtp = (token) => {
    return this.post(`${triggerOtpUrl}${token}`);
  };

  public ValidateOtp = ({ token, payload }): Promise<ApiKeyCreateResponse> => {
    return this.post(`${validateOtpUrl}${token}`, payload);
  };

  public deleteIdp = (idpId: string) => {
    return this.delete(samlUrl(idpId));
  };

  public upsertIdpWithSSO = (payload: UpsertSsoPayload) => {
    if ((payload as UpdateSsoPayload).idpId) {
      return this.updateIdpWithSSO(payload as UpdateSsoPayload);
    }

    return this.createIdpWithSSO(payload as CreateSsoPayload);
  };
}

export const IdpHttpClientApi = new IdpHttpClient();
